import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useTrans } from "../../../hooks";
import { VStack, useToast } from "@chakra-ui/react";
import {
    AppLoader,
    AppPageContent,
    AppPageHeader,
    AppSelectOption,
} from "../../../components";
import { InternshipScheduleSkillList as ListComponent } from "./InternshipScheduleSkillList";
import {
    InternshipScheduleSkill as Entity,
    InternshipScheduleSkillApi as EntityApi,
    Gesture,
    GestureApi,
    Internship,
    InternshipApi,
    InternshipSchedule,
    InternshipScheduleApi,
    SkillCriteria,
    SkillCriteriaApi,
    Training,
} from "../../../apis";
import { APPC, cPath } from "../../../config";

export const InternshipScheduleSkillPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { id: internshipId, internshipScheduleId } = useParams<{
        id: string;
        internshipScheduleId: string;
    }>();

    // state & const
    const [loading, setLoading] = useState(true);
    const [loadingList, setLoadingList] = useState(true);
    const [internship, setInternship] = useState<Internship | null>(null);
    const [internshipSchedule, setInternshipSchedule] =
        useState<InternshipSchedule | null>(null);
    const [skillCriterias, setSkillCriterias] = useState<any[]>([]);
    const [assignListGesturesLoaded, setAssignListGesturesLoaded] =
        useState<boolean>(false);
    const [gestureOptions, setGestureOptions] = useState<AppSelectOption[]>([]);
    const [assignListGestures, setAssignListGestures] = useState({});

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const { table, pageIndex, pagesCount, pageSize, setPagesCount } =
        useAppTable("name", "asc", APPC.PAGE_SIZES[3]);

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const loadAssignListGestures = async () => {
        const params = {
            [`order["skillCriteria.skill.name]`]: "asc",
            pagination: false,
        };
        if (internshipScheduleId) {
            params[`internshipSchedule.id`] = internshipScheduleId;
        }

        await EntityApi.getCollection<Entity>(1, params).then(
            ({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    const gestureMap = {};
                    response.items.forEach((item) => {
                        const criteriaId = (
                            item?.skillCriteria as SkillCriteria
                        )?.id;
                        gestureMap[criteriaId] = {
                            gestures: item.gestures,
                            internshipScheduleSkillId: item.id,
                        };
                    });
                    setAssignListGestures(gestureMap);
                }
            }
        );
        setAssignListGesturesLoaded(true);
    };

    const loadSkillCriterias = () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            "skill.training.id": `${(internship?.training as Training).id}`,
            [`order[skill.name + 0]`]: "asc",
            [`order[name + 0]`]: "asc",
        };

        SkillCriteriaApi.getCollection<SkillCriteria>(
            pageIndex + 1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    const updatedSkillCriterias = response.items.map(
                        (criteria) => {
                            let gestures = [];
                            let internshipScheduleSkillId = 0;

                            const foundGestures =
                                assignListGestures[Number(criteria.id)];
                            if (foundGestures) {
                                gestures = foundGestures.gestures.map(
                                    (opt) => ({
                                        value: opt["@id"],
                                        label: opt.name,
                                    })
                                );
                                internshipScheduleSkillId =
                                    foundGestures.internshipScheduleSkillId;
                            }

                            return {
                                ...criteria,
                                gestures,
                                internshipScheduleSkillId,
                            };
                        }
                    );
                    setSkillCriterias(updatedSkillCriterias);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const loadGestureOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };

        GestureApi.getCollection<Gesture>(1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        }).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                setGestureOptions(
                    response?.items?.map((e) => ({
                        value: GestureApi.toResourceIRI(e.id),
                        label: e.name,
                    })) || []
                );
            }
        });
    };

    // useEffect
    useEffect(() => {
        loadAssignListGestures();
        loadGestureOptions();
    }, []);

    useEffect(() => {
        if (assignListGesturesLoaded && internship) {
            loadSkillCriterias();
        }
    }, [pageIndex, pageSize, assignListGesturesLoaded, internship]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            internshipId
                ? InternshipApi.getItem<Internship>(internshipId)
                : Promise.resolve(null),
            internshipScheduleId
                ? InternshipScheduleApi.getItem<InternshipSchedule>(
                      internshipScheduleId
                  )
                : Promise.resolve(null),
        ])
            .then(([internshipRes, scheduleRes]) => {
                if (internshipRes) {
                    if (internshipRes.errorMessage) {
                        toast({
                            title: internshipRes.errorMessage,
                            status: "error",
                        });
                    } else if (internshipRes.isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (internshipRes.response !== null) {
                        setInternship(internshipRes.response);
                    }
                }
                if (scheduleRes) {
                    if (scheduleRes.errorMessage) {
                        toast({
                            title: scheduleRes.errorMessage,
                            status: "error",
                        });
                    } else if (scheduleRes.isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (scheduleRes.response !== null) {
                        setInternshipSchedule(scheduleRes.response);
                    }
                }
            })
            .finally(() => setLoading(false));
    }, [internshipId, internshipScheduleId]);

    if (loading) {
        return <AppLoader />;
    }

    return (
        <>
            <AppPageHeader
                title={t("padm.InternshipScheduleSkillPage:text.pageTitle")}
                breadcrumb={
                    internship?.name && internshipSchedule?.name
                        ? [
                              {
                                  label: internship?.name,
                                  url: cPath("ADMIN.INTERNSHIP_PAGE"),
                              },
                              {
                                  label: internshipSchedule?.name,
                                  url: cPath("ADMIN.INTERNSHIP_SCHEDULE_PAGE", {
                                      id: internshipId,
                                  }),
                              },
                          ]
                        : []
                }
            />
            <AppPageContent>
                <VStack p={6}>
                    <ListComponent
                        table={table}
                        paging={paging}
                        list={skillCriterias}
                        setList={setSkillCriterias}
                        gestureOptions={gestureOptions}
                        loadingList={loadingList}
                    />
                </VStack>
            </AppPageContent>
        </>
    );
};
